"use client";

import { Container } from "@/components/container";
import { Card, CardContent } from "@/components/ui/card";
import { MotionDiv, useInView } from "@/lib/framer";
import { useRef } from "react";
import { HelpCircleIcon } from "lucide-react";

function FaqCard({ question, answer, delay }: { question: string; answer: React.ReactNode; delay: number }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  return (
    <MotionDiv
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      transition={{ duration: 0.5, delay }}
      className="h-full"
    >
      <Card className="group h-full transition-all duration-300 hover:shadow-md">
        <CardContent className="flex h-full flex-col pt-6">
          <h3 className="text-foreground mb-3 flex items-start gap-x-3 text-lg">
            <span className="flex h-[1lh] items-center">
              <HelpCircleIcon className="text-golden-500 size-4 shrink-0" />
            </span>
            {question}
          </h3>
          <div className="text-muted-foreground text-sm leading-relaxed">{answer}</div>
        </CardContent>
      </Card>
    </MotionDiv>
  );
}

export default function FaqSection() {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.2 });

  return (
    <Container className="py-24">
      <MotionDiv
        ref={sectionRef}
        initial={{ opacity: 0 }}
        animate={isInView ? { opacity: 1 } : { opacity: 0 }}
        transition={{ duration: 0.5 }}
        className="space-y-12"
      >
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-foreground text-4xl tracking-tight sm:text-5xl">Frequently Asked Questions</h2>
          <p className="text-muted-foreground mt-4 text-lg">
            Have questions about Git Digest? We're here to help. If you need additional information,{" "}
            <a href="mailto:support@gitdigest.ai" className="border-golden-500 hover:text-golden-500 border-b">
              contact our team
            </a>
            .
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          <FaqCard
            question="How does Git Digest drive team productivity?"
            answer={
              <>
                Git Digest transforms daily standups into efficient, asynchronous updates by automatically generating
                comprehensive code change summaries. Our AI-powered analysis delivers detailed insights via email or
                Slack, saving an average of 75 minutes per developer weekly while improving team alignment and
                decision-making.
              </>
            }
            delay={0.1}
          />
          <FaqCard
            question="What's the ROI of implementing Git Digest?"
            answer={
              <>
                Based on industry metrics, Git Digest generates approximately $10,000 in annual productivity savings per
                team member by eliminating redundant meetings and streamlining code review processes. With an annual
                investment of $199, the return on investment is substantial and measurable.
              </>
            }
            delay={0.2}
          />
          <FaqCard
            question="How quickly can we implement Git Digest?"
            answer={
              <>
                Implementation takes under 5 minutes. After connecting your GitHub repositories, you can immediately
                generate your first insights or configure automated daily/weekly digests. Our enterprise-ready solution
                ensures seamless integration with your existing workflow.
              </>
            }
            delay={0.3}
          />
          <FaqCard
            question="What security measures are in place?"
            answer={
              <>
                We implement enterprise-grade security protocols. While we require repository access for diff analysis,
                we never store source code. All summaries are encrypted at rest, and we maintain strict compliance with
                industry security standards to protect your intellectual property.
              </>
            }
            delay={0.4}
          />
          <FaqCard
            question="What analytics and insights are available?"
            answer={
              <>
                Our analytics suite provides comprehensive metrics including velocity trends, code distribution
                analysis, and activity patterns. These insights enable data-driven decisions about team productivity,
                resource allocation, and development cycles through intuitive visualizations.
              </>
            }
            delay={0.5}
          />
          <FaqCard
            question="How does Git Digest differentiate from competitors?"
            answer={
              <>
                Git Digest pioneers AI-powered code analysis that translates technical changes into clear,
                business-relevant insights. Our platform uniquely bridges the gap between development activities and
                business objectives, enabling efficient, asynchronous team coordination without traditional standups.
              </>
            }
            delay={0.6}
          />
          <FaqCard
            question="What are the GitHub requirements?"
            answer={
              <>
                Git Digest requires GitHub account authentication and organization owner/admin privileges to ensure
                proper access management. This requirement maintains security standards while enabling comprehensive
                repository analysis and team insights.
              </>
            }
            delay={0.7}
          />
          <FaqCard
            question="How accurate are the AI-generated summaries?"
            answer={
              <>
                Our AI models are continuously refined to deliver high-quality, contextual summaries of code changes.
                While we maintain exceptional accuracy standards, we recommend using the insights as a complement to
                existing development processes rather than a complete replacement.
              </>
            }
            delay={0.8}
          />
        </div>
      </MotionDiv>
    </Container>
  );
}
